/** Bounded local media contract. Pixel metadata supplied by a client is advisory, not codec verification. */
export const MEDIA_LIMITS = Object.freeze({
  photos: 8,
  photoBytes: 2 * 1024 * 1024,
  videoBytes: 12 * 1024 * 1024,
  totalBytes: 20 * 1024 * 1024,
  seconds: 60,
  edge: 1600,
});
/** Validate ordered attachment kinds before decoding/storage. Ordinary posts allow one
 * video among eight attachments; a reel remains photos or a single prepared video. */
export function validateMediaSelection(kinds, { reel = false } = {}) {
  if (!Array.isArray(kinds) || kinds.length > MEDIA_LIMITS.photos)
    throw new Error('Add up to eight attachments.');
  if (kinds.some((kind) => !['image', 'video'].includes(kind)))
    throw new Error('Unsupported media kind.');
  const videos = kinds.filter((kind) => kind === 'video').length;
  if (videos > 1) throw new Error('Add at most one video.');
  if (reel && videos && kinds.length !== 1)
    throw new Error('Use photos together, or one video on its own for a reel.');
}
// Numeric pixel coefficients are authoritative for every preview and export.
export const FILTERS = Object.freeze({
  original: Object.freeze({ saturation: 1, contrast: 1, brightness: 1 }),
  vivid: Object.freeze({ saturation: 1.3, contrast: 1.08, brightness: 1 }),
  warm: Object.freeze({
    saturation: 1,
    contrast: 1,
    brightness: 1,
    gain: Object.freeze([1.06, 1.01, 0.91]),
    offset: Object.freeze([8, 3, 0]),
  }),
  mono: Object.freeze({ saturation: 0, contrast: 1.12, brightness: 1 }),
  cinema: Object.freeze({ saturation: 0.8, contrast: 1.15, brightness: 0.95 }),
});
/** Mutate RGBA bytes in place; preserve alpha. Unknown presets fail rather than silently exporting different pixels. */
export function filterPixels(data, name) {
  if (!Object.hasOwn(FILTERS, name)) throw new Error('Unknown media filter.');
  const policy = FILTERS[name];
  if (name === 'original') return data;
  for (let i = 0; i < data.length; i += 4) {
    const gray = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    for (let channel = 0; channel < 3; channel++) {
      let value = gray + (data[i + channel] - gray) * policy.saturation;
      value = value * (policy.gain?.[channel] ?? 1) + (policy.offset?.[channel] ?? 0);
      data[i + channel] = ((value - 128) * policy.contrast + 128) * policy.brightness;
    }
  }
  return data;
}
/** Cache identity includes unique source-entry IDs, order and all pixel/audio edits; source objects and URLs are transient. */
export function mediaExportKey({ title, music, pack, reel, entries }) {
  if (entries.some((entry) => typeof entry.id !== 'string' || !entry.id))
    throw new Error('Media entry identity required.');
  return JSON.stringify({
    title,
    music,
    pack: pack.id,
    reel,
    entries: entries.map(({ source, url, ...entry }) => entry),
  });
}
export const LAYOUTS = ['auto', 'grid', 'carousel', 'feature'];
/** Resolve gallery geometry without changing authored order. Dimensions are advisory pixels;
 * missing/invalid legacy dimensions use square geometry. Explicit lead indices must name an
 * image in this gallery; invalid choices fail rather than promoting an unrelated asset. */
export function resolveMediaLayout(media = [], { layout = 'auto', leadMediaIndex = null } = {}) {
  if (!LAYOUTS.includes(layout)) throw new Error('Choose an available media layout.');
  if (
    leadMediaIndex !== null &&
    (!Number.isInteger(leadMediaIndex) ||
      leadMediaIndex < 0 ||
      leadMediaIndex >= media.length ||
      media[leadMediaIndex]?.kind !== 'image')
  )
    throw new Error('Choose a key photo from this post.');
  const ratios = media.map(({ width, height }) =>
    Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
      ? width / height
      : 1,
  );
  const imageIndices = media.flatMap((item, index) => (item.kind === 'image' ? [index] : []));
  // A landscape photo can anchor mixed orientations without inferring subject importance.
  const leadIndex =
    leadMediaIndex ??
    imageIndices.reduce(
      (best, index) => (ratios[index] > ratios[best] ? index : best),
      imageIndices[0] ?? 0,
    );
  const portrait = ratios.every((ratio) => ratio < 0.9);
  const landscape = ratios.every((ratio) => ratio > 1.2);
  const square = ratios.every((ratio) => ratio >= 0.9 && ratio <= 1.2);
  let resolved = layout;
  if (media.some((item) => item.kind === 'video')) resolved = 'carousel';
  else if (layout === 'auto') {
    if (leadMediaIndex !== null && media.length > 1) resolved = 'feature';
    else if (media.length >= 3 && media.length <= 4 && !(portrait || landscape || square))
      resolved = 'feature';
    else resolved = 'grid';
  }
  const variant =
    media.length < 2
      ? 'single'
      : resolved === 'carousel'
        ? 'carousel'
        : resolved === 'feature'
          ? 'hero'
          : media.length === 2
            ? portrait
              ? 'pair-portrait'
              : 'pair-landscape'
            : media.length === 3
              ? 'triptych'
              : media.length === 4
                ? 'quad'
                : 'grid';
  return { layout: resolved, variant, leadIndex, ratios };
}
export const MUSIC = ['none', 'quiet-morning', 'coastal-pulse', 'open-sky'];
/** Cropping coordinates in the oriented, rotated source. Pan values are normalized 0..1. */
export function cropWindow(width, height, ratio = 'original', zoom = 1, panX = 0.5, panY = 0.5) {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0)
    throw new Error('Invalid media dimensions.');
  const aspect = ratio === 'original' ? width / height : Number(ratio);
  if (!Number.isFinite(aspect) || aspect <= 0) throw new Error('Invalid crop ratio.');
  const z = Math.max(1, Math.min(3, Number(zoom) || 1));
  let w = width,
    h = w / aspect;
  if (h > height) {
    h = height;
    w = h * aspect;
  }
  w /= z;
  h /= z;
  const clamp = (v) => Math.max(0, Math.min(1, Number(v) || 0));
  return {
    x: (width - w) * clamp(panX),
    y: (height - h) * clamp(panY),
    width: w,
    height: h,
  };
}
export function outputSize(width, height, maxEdge = 1600) {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}
